import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaStar, FaCheckCircle, FaTimesCircle, FaHotel, FaGlobeAfrica, FaPlane } from "react-icons/fa";
import "../css/styles.css";

const UltimateLuxurySafari = () => {
  return (
    <div className="safari-detail-page">
      {/* ===== HERO ===== */}
      <section className="safari-detail-hero">
        <div className="safari-detail-hero-bg">
          <img
            src="https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1400/v1776348819/Ultimate_East_Africa_Luxury_Safari_d9opmh.jpg"
            alt="Ultimate East Africa Luxury Safari"
          />
          <div className="safari-detail-hero-overlay" />
        </div>
        <div className="safari-detail-hero-content">
          <span className="safari-detail-eyebrow">East Africa Luxury Safari</span>
          <h1>Ultimate East Africa Luxury Safari</h1>
          <p className="safari-detail-hero-sub">
            14–16 days of exclusive wildlife, world-class lodges, private transfers, and a luxury beach finale in Diani or Zanzibar.
          </p>
          <div className="safari-detail-hero-buttons">
            <Link to="/contact" className="hero-cta primary">
              Plan Your Luxury Safari <FaArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== OVERVIEW ===== */}
      <section className="safari-detail-overview">
        <div className="safari-detail-section-header">
          <span className="section-tag">Safari Overview</span>
          <h2>East Africa at Its Finest</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-text">
          <p>
            The Ultimate East Africa Luxury Safari is a 14–16-day journey across Kenya and Tanzania, offering the very best of wildlife, scenery, and comfort.
          </p>
          <p>
            Travel in luxury safari vehicles, stay in top-tier lodges and tented camps, and enjoy tailor-made experiences at every step. Witness the Big Five, marvel at the Ngorongoro Crater, explore the Serengeti plains, and finish with a luxury beach retreat in Diani or Zanzibar.
          </p>
          <p>
            This safari combines adventure, comfort, and exclusivity — perfect for travelers seeking East Africa at its finest.
          </p>
        </div>
      </section>

      {/* ===== HIGHLIGHTS ===== */}
      <section className="safari-detail-highlights">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Highlights</span>
          <h2>Safari Highlights</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-highlights-list">
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Explore Kenya's Maasai Mara, Amboseli, Lake Nakuru, and Tanzania's Serengeti and Ngorongoro</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>See lions, elephants, rhinos, giraffes, zebras, and other wildlife</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Optional private light aircraft transfers between parks for ultimate convenience</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Sunrise hot air balloon safari over Maasai Mara or Serengeti (optional)</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Private bush dinners, heli-safaris, or cultural visits available</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>End your safari with 4 nights on the white sandy beaches of Diani or Zanzibar</p>
          </div>
        </div>
      </section>

      {/* ===== AT A GLANCE ===== */}
      <section className="safari-detail-glance">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Quick Info</span>
          <h2>Safari at a Glance</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-glance-grid">
          <div className="safari-detail-glance-card">
            <FaClock className="safari-detail-glance-icon" />
            <h4>Duration</h4>
            <p>14–16 Days / 13–15 Nights</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Start / End</h4>
            <p>Nairobi / Nairobi or Zanzibar</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaGlobeAfrica className="safari-detail-glance-icon" />
            <h4>Destinations</h4>
            <p>Maasai Mara &ndash; Amboseli &ndash; Lake Nakuru &ndash; Serengeti &ndash; Ngorongoro &ndash; Diani / Zanzibar</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaCalendarAlt className="safari-detail-glance-icon" />
            <h4>Best Time to Travel</h4>
            <p>Jun–Oct, Dec–Mar</p>
          </div>
        </div>
      </section>

      {/* ===== ITINERARY ===== */}
      <section className="safari-detail-itinerary">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Day by Day</span>
          <h2>Day-by-Day Itinerary</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-days">
          {/* Day 1 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">1</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 1 — Arrival in Nairobi</h3>
              <p>
                Private pickup from the airport and transfer to Hemingways Nairobi or similar luxury hotel. Relax or explore Nairobi's top sights.
              </p>
              <p className="safari-detail-day-note">Overnight in Nairobi.</p>
            </div>
          </div>

          {/* Day 2 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">2</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 2 — Nairobi to Maasai Mara <span className="safari-detail-flight-badge"><FaPlane /> Optional Flight</span></h3>
              <p>
                Drive to Maasai Mara in a comfortable 4x4 safari vehicle (approx. 5–6 hours). Optional: upgrade to private light aircraft transfer for convenience.
              </p>
              <p>Afternoon game drive in the Mara.</p>
              <p className="safari-detail-day-note">Overnight in the Maasai Mara.</p>
            </div>
          </div>

          {/* Day 3 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">3</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 3 — Full Day in Maasai Mara</h3>
              <p>
                Morning and afternoon game drives with your private guide. Optional sunrise hot air balloon safari or visit to a Maasai village for cultural immersion.
              </p>
              <p className="safari-detail-day-note">Overnight in the Maasai Mara.</p>
            </div>
          </div>

          {/* Day 4 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">4</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 4 — Maasai Mara to Amboseli <span className="safari-detail-flight-badge"><FaPlane /> Optional Flight</span></h3>
              <p>
                Drive or opt for private aircraft transfer to Amboseli. Enjoy lunch at your lodge, followed by an afternoon game drive with Mount Kilimanjaro as a backdrop.
              </p>
              <p className="safari-detail-day-note">Overnight in Amboseli.</p>
            </div>
          </div>

          {/* Day 5 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">5</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 5 — Full Day in Amboseli</h3>
              <p>
                Morning and afternoon game drives to spot elephants, lions, giraffes, and more. Optional private bush dining experience in the evening.
              </p>
              <p className="safari-detail-day-note">Overnight in Amboseli.</p>
            </div>
          </div>

          {/* Day 6 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">6</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 6 — Amboseli to Lake Nakuru <span className="safari-detail-flight-badge"><FaPlane /> Optional Flight</span></h3>
              <p>
                Travel to Lake Nakuru, famous for flamingos and rhinos. Afternoon game drive around the lake.
              </p>
              <p className="safari-detail-day-note">Overnight in Lake Nakuru.</p>
            </div>
          </div>

          {/* Day 7 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">7</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 7 — Lake Nakuru to Serengeti <span className="safari-detail-flight-badge"><FaPlane /> Optional Flight</span></h3>
              <p>
                Cross the border into Tanzania and continue to the legendary Serengeti National Park. Arrive and begin your first game drive across the famous plains.
              </p>
              <p className="safari-detail-day-note">Overnight in the Serengeti.</p>
            </div>
          </div>

          {/* Days 8–9 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">8‑9</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Days 8–9 — Full Days in Serengeti</h3>
              <p>
                Morning and afternoon game drives to see lions, leopards, cheetahs, elephants, giraffes, and more. Optional hot air balloon safari for breathtaking aerial views.
              </p>
              <p className="safari-detail-day-note">Overnight in the Serengeti.</p>
            </div>
          </div>

          {/* Day 10 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">10</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 10 — Serengeti to Ngorongoro <span className="safari-detail-flight-badge"><FaPlane /> Optional Flight</span></h3>
              <p>
                Morning game drive before traveling to the Ngorongoro Conservation Area. Enjoy sunset at your lodge overlooking the crater.
              </p>
              <p className="safari-detail-day-note">Overnight in Ngorongoro.</p>
            </div>
          </div>

          {/* Day 11 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">11</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 11 — Ngorongoro Crater Safari</h3>
              <p>
                Descend into the crater for a full-day wildlife experience. The crater floor hosts an incredible concentration of wildlife including lions, elephants, buffalo, zebras, and sometimes the rare black rhino. Picnic lunch amid stunning scenery.
              </p>
              <p className="safari-detail-day-note">Overnight in Ngorongoro.</p>
            </div>
          </div>

          {/* Day 12 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">12</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 12 — Ngorongoro to Diani / Zanzibar <span className="safari-detail-flight-badge"><FaPlane /> Flight</span></h3>
              <p>
                Transfer to Mombasa or Zanzibar for your luxury beach stay. Arrive at your oceanfront resort and relax as the Indian Ocean unfolds before you.
              </p>
              <p className="safari-detail-day-note">Overnight in Diani Beach / Zanzibar.</p>
            </div>
          </div>

          {/* Days 13–15 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">13‑15</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Days 13–15 — Beach Leisure</h3>
              <p>
                Enjoy 3 full days of rest and relaxation at your luxury beach resort.
              </p>
              <p><strong>Optional activities include:</strong></p>
              <ul className="safari-detail-day-list">
                <li>Snorkeling or diving in marine parks</li>
                <li>Sunset dhow cruises</li>
                <li>Spa treatments and wellness experiences</li>
                <li>Private island tours</li>
              </ul>
              <p className="safari-detail-day-note">Overnight in Diani Beach / Zanzibar.</p>
            </div>
          </div>

          {/* Day 16 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">16</div>
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 16 — Departure</h3>
              <p>
                Private transfer to the airport for your international flight.
              </p>
              <p className="safari-detail-day-note">
                Your ultimate East Africa luxury safari comes to an end.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ACCOMMODATION ===== */}
      <section className="safari-detail-accommodation">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Where You'll Stay</span>
          <h2>Accommodation Examples — Luxury</h2>
          <div className="safari-detail-line" />
        </div>

        {/* Nairobi */}
        <h3 className="safari-detail-accom-region-title">Nairobi</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Hemingways Nairobi</li>
              <li><FaHotel /> Eka Hotel</li>
            </ul>
          </div>
        </div>

        {/* Maasai Mara */}
        <h3 className="safari-detail-accom-region-title">Maasai Mara</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Angama Mara</li>
              <li><FaHotel /> Mahali Mzuri</li>
            </ul>
          </div>
        </div>

        {/* Amboseli */}
        <h3 className="safari-detail-accom-region-title">Amboseli</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Ol Tukai Lodge</li>
              <li><FaHotel /> Amboseli Serena Safari Lodge</li>
            </ul>
          </div>
        </div>

        {/* Lake Nakuru */}
        <h3 className="safari-detail-accom-region-title">Lake Nakuru</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Lake Nakuru Sopa Lodge</li>
              <li><FaHotel /> Sarova Lion Hill Game Lodge</li>
              <li><FaHotel /> The Cliff</li>
            </ul>
          </div>
        </div>

        {/* Serengeti */}
        <h3 className="safari-detail-accom-region-title">Serengeti</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Serengeti Serena Safari Lodge</li>
              <li><FaHotel /> Four Seasons Safari Lodge Serengeti</li>
            </ul>
          </div>
        </div>

        {/* Ngorongoro */}
        <h3 className="safari-detail-accom-region-title">Ngorongoro</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Ngorongoro Crater Lodge</li>
              <li><FaHotel /> Ngorongoro Serena Safari Lodge</li>
            </ul>
          </div>
        </div>

        {/* Diani / Zanzibar */}
        <h3 className="safari-detail-accom-region-title">Diani / Zanzibar</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury Beach</div>
            <ul>
              <li><FaHotel /> Almanara Luxury Villas</li>
              <li><FaHotel /> Baraza Resort</li>
            </ul>
          </div>
        </div>

        <p className="safari-detail-accom-note">
          All lodges are carefully selected for luxury, comfort, and unforgettable experiences. Similar properties of the same standard may be used depending on availability.
        </p>
      </section>

      {/* ===== INCLUDED / EXCLUDED ===== */}
      <section className="safari-detail-inclusions">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">What to Expect</span>
          <h2>What's Included</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-inclusions-grid">
          <div className="safari-detail-inclusions-card included">
            <h3><FaCheckCircle /> Included</h3>
            <ul>
              <li>Transport in luxury 4x4 safari vehicles with professional guide</li>
              <li>Park entrance fees</li>
              <li>Luxury accommodations</li>
              <li>Meals and drinks as specified</li>
              <li>Bottled water during game drives</li>
              <li>Optional private light aircraft transfers (extra cost)</li>
            </ul>
          </div>
          <div className="safari-detail-inclusions-card excluded">
            <h3><FaTimesCircle /> Not Included</h3>
            <ul>
              <li>International flights</li>
              <li>Travel insurance</li>
              <li>Visa fees</li>
              <li>Personal expenses and tips</li>
              <li>Optional activities not pre-booked (balloon safari, heli-safari, private bush dining)</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="safari-detail-cta">
        <div className="safari-detail-cta-content">
          <h2>Customize This Safari</h2>
          <p>
            Upgrade to private aircraft transfers between all parks, extend your beach stay or include private islands, arrange private chefs, heli-safaris, or exclusive experiences, and upgrade to ultra-luxury suites or private villas.
          </p>
          <div className="safari-detail-cta-buttons">
            <Link to="/contact?safari=Ultimate East Africa Luxury Safari" className="hero-cta primary">
              Plan Your Luxury Safari <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/tours" className="hero-cta secondary">
              View More Safaris
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UltimateLuxurySafari;
